import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  Mail,
  MessageCircle,
  MessageSquareText,
  Pencil,
  PlayCircle,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  CommunicationIntegration,
  CommunicationIntegrationKind,
} from "@/types/communication-integration";
import {
  useSetIntegrationEnabled,
  useTestIntegration,
} from "../hooks/communication-integrations.hooks";
import { HealthBadge } from "./HealthBadge";
import { IntegrationSummary } from "./IntegrationSummary";
import { EmailIntegrationDialog } from "./EmailIntegrationDialog";
import { TwilioIntegrationDialog } from "./TwilioIntegrationDialog";
import { WhatsappIntegrationDialog } from "./WhatsappIntegrationDialog";
import { WhatsappFlowOperationsPanel } from "./WhatsappFlowOperationsPanel";
import { WhatsappRuntimeSettingsPanel } from "./WhatsappRuntimeSettingsPanel";
import { WhatsappTemplateOperationsPanel } from "./WhatsappTemplateOperationsPanel";
import { DeleteIntegrationDialog } from "./DeleteIntegrationDialog";
import { DisableIntegrationDialog } from "./DisableIntegrationDialog";

interface IntegrationCardProps {
  hotelId: string;
  kind: CommunicationIntegrationKind;
  integration: CommunicationIntegration | null;
}

const KIND_ICON: Record<CommunicationIntegrationKind, React.ReactNode> = {
  EMAIL: <Mail className="size-5 text-skyline-2" />,
  TWILIO: <MessageSquareText className="size-5 text-skyline-2" />,
  META_WHATSAPP: <MessageCircle className="size-5 text-skyline-2" />,
};

export const IntegrationCard: React.FC<IntegrationCardProps> = ({
  hotelId,
  kind,
  integration,
}) => {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [disableOpen, setDisableOpen] = React.useState(false);

  const { mutate: setEnabled, isPending: isToggling } =
    useSetIntegrationEnabled(hotelId);
  const { mutate: testIntegration, isPending: isProbing } =
    useTestIntegration(hotelId);

  const isConfigured = integration !== null;
  const isEnabled = integration?.enabled ?? false;
  const isWhatsapp = kind === "META_WHATSAPP";
  const showTestButton = isConfigured;

  const handleSwitchChange = (next: boolean) => {
    if (next) {
      setEnabled(
        { kind, enabled: true },
        {
          onSuccess: () =>
            toast.success(t("communicationChannels.enable.success")),
          onError: () => toast.error(t("communicationChannels.enable.error")),
        },
      );
    } else {
      setDisableOpen(true);
    }
  };

  const handleTest = () => {
    testIntegration(kind, {
      onSuccess: (result) => {
        if (result.status === "HEALTHY") {
          toast.success(
            t(
              isWhatsapp
                ? "communicationChannels.test.whatsappHealthy"
                : "communicationChannels.test.healthy",
            ),
          );
        } else if (result.status === "UNHEALTHY_AUTH") {
          toast.error(
            t(
              isWhatsapp
                ? "communicationChannels.test.whatsappUnhealthyAuth"
                : "communicationChannels.test.unhealthyAuth",
            ),
            {
              description: result.error ?? undefined,
            },
          );
        } else {
          toast.error(
            t(
              isWhatsapp
                ? "communicationChannels.test.whatsappUnhealthyConnection"
                : "communicationChannels.test.unhealthyConnection",
            ),
            {
              description: result.error ?? undefined,
            },
          );
        }
      },
      onError: () => {
        toast.error(
          t(
            isWhatsapp
              ? "communicationChannels.test.whatsappError"
              : "communicationChannels.test.error",
          ),
        );
      },
    });
  };

  const renderEditDialog = () => {
    if (kind === "EMAIL") {
      return (
        <EmailIntegrationDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          hotelId={hotelId}
          existing={integration}
        />
      );
    }
    if (kind === "TWILIO") {
      return (
        <TwilioIntegrationDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          hotelId={hotelId}
          existing={integration}
        />
      );
    }
    return (
      <WhatsappIntegrationDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        hotelId={hotelId}
        existing={integration}
      />
    );
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {KIND_ICON[kind]}
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-base font-semibold text-foreground leading-tight">
                {t(`communicationChannels.kinds.${kind}.label`)}
              </span>
              {renderStatusBadge(isConfigured, isEnabled, t)}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isConfigured && (
              <Button
                type="button"
                variant="new_default"
                size="sm"
                onClick={() => setEditOpen(true)}
              >
                {t("communicationChannels.actions.setUp")}
              </Button>
            )}

            {isConfigured && (
              <>
                {showTestButton && (
                  <Button
                    type="button"
                    variant="new_outline"
                    size="sm"
                    disabled={isProbing}
                    onClick={handleTest}
                  >
                    {isProbing ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <PlayCircle className="size-4" />
                    )}
                    {t(
                      isWhatsapp
                        ? "communicationChannels.actions.retryProvisioning"
                        : "communicationChannels.actions.test",
                    )}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="new_secondary"
                  size="sm"
                  onClick={() => setEditOpen(true)}
                >
                  <Pencil className="size-4" />
                  {t("communicationChannels.actions.edit")}
                </Button>
                <Switch
                  checked={isEnabled}
                  disabled={isToggling}
                  aria-label={t("communicationChannels.actions.toggleAria")}
                  onCheckedChange={handleSwitchChange}
                />
                <Button
                  type="button"
                  variant="new_outline"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="size-4" />
                  {t("communicationChannels.actions.delete")}
                </Button>
              </>
            )}
          </div>
        </div>

        {isConfigured && integration && (
          <div className="flex flex-col gap-1 border-t pt-4">
            <div className="flex flex-wrap items-center gap-3">
              <HealthBadge status={integration.healthStatus} />
              <span className="text-xs text-muted-foreground">
                {integration.lastVerifiedAt
                  ? t("communicationChannels.health.lastVerifiedAt", {
                      when: formatTimestamp(integration.lastVerifiedAt),
                    })
                  : t("communicationChannels.health.neverTested")}
              </span>
            </div>
            {integration.lastError && (
              <p
                className="text-xs text-destructive line-clamp-2"
                title={integration.lastError}
              >
                {integration.lastError}
              </p>
            )}
          </div>
        )}

        {isConfigured && integration && (
          <IntegrationSummary integration={integration} />
        )}

        {isWhatsapp && isConfigured && (
          <>
            <WhatsappRuntimeSettingsPanel
              hotelId={hotelId}
              integrationEnabled={isEnabled}
            />
            <WhatsappTemplateOperationsPanel hotelId={hotelId} />
            <WhatsappFlowOperationsPanel hotelId={hotelId} />
          </>
        )}

        {!isConfigured && (
          <p className="text-sm text-muted-foreground">
            {t(`communicationChannels.kinds.${kind}.placeholderDescription`)}
          </p>
        )}
      </CardContent>

      {renderEditDialog()}

      {isConfigured && (
        <>
          <DeleteIntegrationDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            hotelId={hotelId}
            kind={kind}
          />
          <DisableIntegrationDialog
            open={disableOpen}
            onOpenChange={setDisableOpen}
            hotelId={hotelId}
            kind={kind}
          />
        </>
      )}
    </Card>
  );
};

const formatTimestamp = (iso: string): string => {
  try {
    return format(new Date(iso), "MMM d, HH:mm");
  } catch {
    return iso;
  }
};

const renderStatusBadge = (
  isConfigured: boolean,
  isEnabled: boolean,
  t: (key: string) => string,
): React.ReactNode => {
  if (!isConfigured) {
    return (
      <Badge variant="new_secondary" className="w-fit">
        {t("communicationChannels.status.notConfigured")}
      </Badge>
    );
  }
  if (isEnabled) {
    return (
      <Badge variant="success" className="w-fit">
        {t("communicationChannels.status.enabled")}
      </Badge>
    );
  }
  return (
    <Badge variant="new_secondary" className="w-fit">
      {t("communicationChannels.status.disabled")}
    </Badge>
  );
};
